/**
 * UnifiedDispoList — the Workspace "AUO disposition" tab, now ONE UNIFIED LIST.
 *
 * Owner-confirmed (2026-07-01): entrance DOORS + AUOs + MARKUPS live together in
 * ONE running sequence, numbered to MATCH THE MAP PINS exactly. The number source
 * is the panel's `evalDispoItems` seqNo (doors 1..d, AUOs continue) plus the
 * markup offset (doors + AUOs + i + 1) — byte-identical to the FloorBoundaryTracer
 * pin formula (`entranceMarks.length + auoMarks.length + i + 1`). So map pin == row.
 *
 * This component is PORTALLED from StaffTechnicalPanel (the marking surface) into
 * the right pane, exactly like the old AuoReviewPanel portal — so it holds the
 * REAL marks + REAL ops, never a bridge redraw. Click any row → expand its FEATURE
 * CARD (each card just DISPLAYS its resolved result — there is NO per-card engine
 * trigger; resolution is driven by the ONE list-level button, below):
 *   · AUO    → the REAL `AuoCardBody` (shared with the inline AuoReviewPanel,
 *              byte-identical), num = the row's UNIFIED seqNo. Full controls +
 *              the authoritative Confirm/Reject path (fixtures[].status → report).
 *   · Door   → door type · set-principal ★ · pada (read from grid) · the ENTRANCE
 *              READING: one finding PER covered pada (report composer's per-pada
 *              rule) — pada code + verbatim effect + entrance-colour swatch, from
 *              the canonical `entranceCodes32` static table (client-side lookup,
 *              no backend). A pada absent from the table shows a calm "no reading".
 *   · Markup → its canonical annotation label (MARKUP_LABEL) · free note/label
 *              editor · the zones/padas it covers (grid-derived, read-only). No
 *              engine verdict (a markup IS the verdict) — SKIPPED by resolution.
 *
 * ONE "Ask engine for disposition" button (owner-chosen 2026-07-02) sits at the top
 * next to the filter chips and resolves the WHOLE list in one action: AUOs → the
 * existing suggest op (`onSuggest` → `onSuggestAuo(0)` / the 'suggest' path); Doors
 * → reveal the client-side per-pada entrance reading (all doors); Markups → skipped.
 * The button shows a busy ("Resolving…") then resolved state.
 *
 * Lightweight filter chips (All · Doors · AUOs · Markups) narrow the one list; the
 * tab badge (owned by ReferenceMediaPane) counts the total. NO nested scroll — the
 * right-pane body is the only scroller. TOKENS ONLY (styling in evaluate-workspace.css).
 */
import { useEffect, useMemo, useState } from 'react';
import { AuoCardBody } from '../AuoReviewPanel';
import type { AuoMark } from '../../lib/auoMarks';
import type { AuoPalette } from '../../lib/auoPalette';
import type { OverrideDisposition } from '../../lib/auoOverrides';
import type { DispoItem } from '../../lib/evalDispositionBridge';
import { MARKUP_LABEL, type MarkupType } from '../../lib/evalMarkups';
import { swatchCss } from '../../lib/auoDisposition';
import { entranceReadingForPada } from '../../lib/entranceCodes32';

/** One markup row in the unified sequence — its seqNo continues after the AUOs. */
export interface UnifiedMarkup {
  id: string;
  type: MarkupType;
  label: string;
  note?: string;
  zones?: string[];
  seqNo: number;
}

const MARKUP_GLYPH: Record<MarkupType, string> = {
  tick: '✓', cross: '✗', question: '?', cloud: '☁', mirror: '▥', arrow: '→', text: 'T',
};

/** Door UI type vocab (label ↔ engine value) — a door is a door (decision #18). */
const DOOR_TYPES: { v: string; l: string }[] = [
  { v: 'main_door', l: 'Main door' },
  { v: 'secondary_door', l: 'Secondary door' },
  { v: 'internal_entry', l: 'Internal door' },
  { v: 'staircase_entry', l: 'Stair door' },
  { v: 'balcony_opening', l: 'Balcony' },
];

function statusChipClass(s?: string): string {
  switch ((s || '').toLowerCase()) {
    case 'confirmed':
    case 'favourable':
      return 'is-good';
    case 'rejected':
    case 'needs remedy':
    case 'critical':
    case 'caution':
      return 'is-warn';
    default:
      return 'is-info';
  }
}

type Filter = 'all' | 'door' | 'auo' | 'markup';
const FILTERS: { v: Filter; l: string }[] = [
  { v: 'all', l: 'All' },
  { v: 'door', l: 'Doors' },
  { v: 'auo', l: 'AUOs' },
  { v: 'markup', l: 'Markups' },
];

const IconChevron = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export interface UnifiedDispoListProps {
  /** doors + AUOs in the unified sequence (the panel's `evalDispoItems`, seqNo set). */
  items: DispoItem[];
  /** the REAL AUO marks — looked up by key to render the shared `AuoCardBody`. */
  auoMarks: AuoMark[];
  /** markups, seqNo continuing after the AUOs. */
  markups: UnifiedMarkup[];
  /** the AUO name palette (for AuoCardBody's searchable picker). */
  palette: AuoPalette | null;
  /** ADMIN — surfaces the per-zone "✎ correct ruling" editor. Session-derived. */
  isAdmin: boolean;
  /** the floor has a grid (≥3-pt boundary) — only then can the engine be asked. */
  canSuggest: boolean;
  /** the marker selected on the map / in this list (two-way reference by number). */
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /* ── AUO ops (bound to floor 0 by the parent) ── */
  onSuggest: () => void;
  onSetName: (id: string, name: string) => void;
  onSetType: (id: string, type: string) => void;
  onSetNameType: (id: string, name: string, type: string) => void;
  onSetZones: (id: string, zones: string[]) => void;
  onSetShape: (id: string, kind: 'rect' | 'circle') => void;
  onRotate: (id: string, deg: number) => void;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onReopen: (id: string) => void;
  onRemove: (id: string) => void;
  onCorrectDisposition?: (
    id: string,
    zone: string,
    patch: { disposition: OverrideDisposition; effect: string; management?: string | null; priorSource?: string | null },
  ) => void;
  onInlineAdd: (name: string) => void;
  /* ── door ops ── */
  onSetDoorType: (id: string, type: string) => void;
  onSetPrincipal: (id: string) => void;
  onRemoveDoor: (id: string) => void;
  /* ── markup ops ── */
  onSetMarkupNote: (id: string, note: string) => void;
  onSetMarkupLabel: (id: string, label: string) => void;
  onRemoveMarkup: (id: string) => void;
}

/** Door feature card — type · set-principal ★ · pada (grid) · the ENTRANCE READING.
 *  The reading is resolved by the ONE list-level button (no per-card trigger): once
 *  `resolved`, it DISPLAYS one finding PER covered pada (the report composer's
 *  per-pada rule) — pada code + verbatim effect + entrance-colour swatch, from the
 *  canonical `entranceCodes32` static table (client-side; never fabricated). */
function DoorCard({
  it,
  resolved,
  onSetDoorType,
  onSetPrincipal,
  onRemoveDoor,
}: {
  it: DispoItem;
  resolved: boolean;
  onSetDoorType: (id: string, type: string) => void;
  onSetPrincipal: (id: string) => void;
  onRemoveDoor: (id: string) => void;
}) {
  const padas = it.padas ?? [];
  return (
    <div className="evalws-dispo-edit">
      <label className="evalws-dispo-field">
        <span>Type</span>
        <select
          value={it.rawType ?? 'main_door'}
          onChange={(e) => onSetDoorType(it.key, e.target.value)}
        >
          {DOOR_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </label>
      <div className="evalws-dispo-field-row">
        <span className="evalws-dispo-read">{it.padaOrZone} · pada (read from grid)</span>
        <button
          type="button"
          className="evalws-dispo-linkbtn"
          disabled={it.principal}
          onClick={() => onSetPrincipal(it.key)}
        >
          {it.principal ? '★ Principal' : 'Set as principal'}
        </button>
      </div>

      {/* ENTRANCE READING — one finding per covered pada (client-side lookup of the
          canonical entranceCodes32 table). Shown once the list-level button has
          resolved; before that a calm prompt points at the one button. */}
      <div className="evalws-dispo-field-row">
        <span className="evalws-dispo-zones-lbl">entrance reading</span>
      </div>
      {!resolved ? (
        <p className="evalws-dispo-hint">
          Press <b>Ask engine for disposition</b> (top) to read this entrance — one
          finding per covered pada.
        </p>
      ) : padas.length === 0 ? (
        <p className="evalws-dispo-hint">No pada covered — off-grid door, no reading.</p>
      ) : (
        <ul className="evalws-door-reading" aria-label="Entrance reading — one per covered pada">
          {padas.map((p) => {
            const r = entranceReadingForPada(p);
            return (
              <li key={p} className="evalws-door-pada">
                <span className="evalws-dispo-zonechip" aria-label={`pada ${p}`}>{p}</span>
                {r ? (
                  <>
                    <span className="evalws-dispo-reason">{r.effect}</span>
                    <span
                      className="evalws-dispo-swatch"
                      title={`Entrance colour: ${r.entrColour} · zone colour: ${r.zoneColour}`}
                    >
                      <span
                        className="evalws-dispo-swatch-dot"
                        aria-hidden="true"
                        style={{ background: swatchCss(r.entrColour) }}
                      />
                      <span className="evalws-dispo-swatch-lbl">{r.entrColour}</span>
                    </span>
                  </>
                ) : (
                  <span className="evalws-dispo-reason is-muted">No reading for this pada.</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <button type="button" className="evalws-dispo-delrow" onClick={() => onRemoveDoor(it.key)}>
        Remove door
      </button>
    </div>
  );
}

/** Markup feature card — canonical label · free note/label editor · covered zones
 *  (grid-derived, read-only). NO engine verdict (a markup IS the verdict). */
function MarkupCard({
  m,
  onSetMarkupNote,
  onSetMarkupLabel,
  onRemoveMarkup,
}: {
  m: UnifiedMarkup;
  onSetMarkupNote: (id: string, note: string) => void;
  onSetMarkupLabel: (id: string, label: string) => void;
  onRemoveMarkup: (id: string) => void;
}) {
  const [note, setNote] = useState(m.note ?? '');
  const [label, setLabel] = useState(m.label ?? '');
  return (
    <div className="evalws-dispo-edit">
      <div className="evalws-dispo-field-row">
        <span className="evalws-dispo-zones">
          <span className="evalws-dispo-zones-lbl">zones</span>
          {m.zones && m.zones.length ? (
            m.zones.map((z) => (
              <span key={z} className="evalws-dispo-zonechip" aria-label={`zone ${z}`}>{z}</span>
            ))
          ) : (
            <span className="evalws-dispo-zonechip is-off">off-grid</span>
          )}
          <span className="evalws-dispo-auto">read from grid</span>
        </span>
      </div>
      <label className="evalws-dispo-field is-stack">
        <span>Label</span>
        <input
          type="text"
          className="evalws-dispo-input"
          value={label}
          placeholder={MARKUP_LABEL[m.type]}
          aria-label="Markup label"
          onChange={(e) => { setLabel(e.target.value); onSetMarkupLabel(m.id, e.target.value); }}
        />
      </label>
      <label className="evalws-dispo-field is-stack">
        <span>Note</span>
        <textarea
          className="evalws-dispo-input"
          value={note}
          rows={2}
          placeholder="Free note for this markup…"
          aria-label="Markup note"
          onChange={(e) => { setNote(e.target.value); onSetMarkupNote(m.id, e.target.value); }}
        />
      </label>
      <button type="button" className="evalws-dispo-delrow" onClick={() => onRemoveMarkup(m.id)}>
        Remove markup
      </button>
    </div>
  );
}

export default function UnifiedDispoList(props: UnifiedDispoListProps) {
  const {
    items, auoMarks, markups, palette, isAdmin, canSuggest, selectedId, onSelect,
    onSuggest, onSetName, onSetType, onSetNameType, onSetZones, onSetShape, onRotate,
    onConfirm, onReject, onReopen, onRemove, onCorrectDisposition, onInlineAdd,
    onSetDoorType, onSetPrincipal, onRemoveDoor,
    onSetMarkupNote, onSetMarkupLabel, onRemoveMarkup,
  } = props;

  const [filter, setFilter] = useState<Filter>('all');
  // single-open accordion: the row key currently expanded (null = all collapsed).
  const [openKey, setOpenKey] = useState<string | null>(null);
  // ONE-button resolution state: `asked` reveals the doors' client-side entrance
  // readings (a pure lookup); `busy` covers the async AUO engine round-trip.
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);

  const auoById = useMemo(
    () => new Map(auoMarks.map((m) => [m.id, m])),
    [auoMarks],
  );
  const paletteItems = palette?.items ?? [];
  const paletteLabelSet = useMemo(
    () => new Set(paletteItems.map((it) => it.label.toLowerCase())),
    [paletteItems],
  );

  // merge doors+AUOs (evalDispoItems) with markups into ONE seqNo-ordered list —
  // the same order the map pins carry, so pin N == row N.
  type Row =
    | { kind: 'door' | 'auo'; seqNo: number; key: string; item: DispoItem }
    | { kind: 'markup'; seqNo: number; key: string; markup: UnifiedMarkup };
  const rows = useMemo<Row[]>(() => {
    const merged: Row[] = [
      ...items.map((it) => ({ kind: it.kind, seqNo: it.seqNo, key: it.key, item: it } as Row)),
      ...markups.map((m) => ({ kind: 'markup' as const, seqNo: m.seqNo, key: m.id, markup: m })),
    ];
    merged.sort((a, b) => a.seqNo - b.seqNo);
    return merged;
  }, [items, markups]);

  const counts = useMemo(() => {
    let door = 0, auo = 0;
    for (const it of items) it.kind === 'door' ? door++ : auo++;
    return { all: rows.length, door, auo, markup: markups.length };
  }, [items, markups, rows.length]);

  const visible = rows.filter((r) => filter === 'all' || r.kind === filter);

  const toggle = (key: string) => {
    onSelect(key);
    setOpenKey((k) => (k === key ? null : key));
  };

  const hasAuos = counts.auo > 0;

  // ONE list-level action: resolve every item in one press.
  //  · AUOs   → the existing suggest op (onSuggest → onSuggestAuo(0) / 'suggest').
  //  · Doors  → reveal the client-side per-pada entrance reading (via `asked`).
  //  · Markups→ skipped (a markup IS its own verdict; no engine ruling).
  const handleAskAll = () => {
    setAsked(true);
    if (hasAuos) {
      setBusy(true);
      onSuggest();
    }
  };

  // Clear busy once the AUO engine round-trip has landed (items re-derived with
  // suggestions) — or immediately if there were no AUOs to resolve.
  useEffect(() => {
    if (!busy) return;
    const auos = rows.filter((r) => r.kind === 'auo');
    if (auos.length === 0 || auos.every((r) => r.kind !== 'markup' && !!r.item.suggestions?.length)) {
      setBusy(false);
    }
  }, [rows, busy]);

  // Safety net: never leave the button spinning if a suggestion never returns.
  useEffect(() => {
    if (!busy) return;
    const t = window.setTimeout(() => setBusy(false), 12000);
    return () => window.clearTimeout(t);
  }, [busy]);

  if (rows.length === 0) {
    return (
      <div className="evalws-dispo">
        <div className="evalws-dispo-empty">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 11l3 3 8-8" /><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /></svg>
          <b>Nothing marked on this floor yet</b>
          <p>Use <b>Mark door</b>, <b>Mark AUO</b> or a <b>Markup</b> on the map. Each lands here in ONE continuous numbered list — <b>doors first</b>, then AUOs, then markups — numbered to match the map pins. Click a row to open its card.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="evalws-dispo">
      {/* header: lightweight filter chips (narrow the ONE list; the badge counts
          total) + the ONE list-level "Ask engine for disposition" button that
          resolves the whole list (AUOs via the engine, doors via the client-side
          entrance reading, markups skipped). */}
      <div className="evalws-dispo-head">
        <div className="evalws-dispo-filter" role="group" aria-label="Filter the disposition list">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              type="button"
              className="evalws-dispo-fchip"
              aria-pressed={filter === f.v}
              onClick={() => setFilter(f.v)}
            >
              {f.l}
              <span className="evalws-dispo-fcnt">{counts[f.v]}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="evalws-dispo-askall"
          aria-busy={busy}
          data-resolved={asked && !busy ? 'true' : undefined}
          disabled={busy || !canSuggest}
          onClick={handleAskAll}
          title={
            canSuggest
              ? 'Resolve the whole list — AUO rulings from the engine + each door’s per-pada entrance reading (markups skipped)'
              : 'Trace the boundary first — the grid must exist before resolving'
          }
        >
          {busy ? 'Resolving…' : asked ? 'Resolved ✓ — ask again' : 'Ask engine for disposition'}
        </button>
      </div>
      {!canSuggest && (
        <p className="evalws-dispo-hint">Trace the boundary first — the grid must exist before resolving.</p>
      )}

      <ul className="evalws-dispo-list" aria-label="Doors, AUOs and markups — one numbered list">
        {visible.map((r) => {
          const open = openKey === r.key;
          const selected = selectedId === r.key;
          const isDoor = r.kind === 'door';
          const isMarkup = r.kind === 'markup';
          const name = isMarkup
            ? (r.markup.label || MARKUP_LABEL[r.markup.type])
            : r.item.name;
          return (
            <li key={r.key}>
              <div className={`evalws-dispo-row${selected ? ' is-sel' : ''}${open ? ' is-editing' : ''}`}>
                <button
                  type="button"
                  className="evalws-dispo-main"
                  aria-pressed={selected}
                  aria-expanded={open}
                  onClick={() => toggle(r.key)}
                >
                  <span className="evalws-dispo-num">{r.seqNo}</span>
                  {isMarkup && (
                    <span className="evalws-dispo-glyph" aria-hidden="true">{MARKUP_GLYPH[r.markup.type]}</span>
                  )}
                  <span className="evalws-dispo-body">
                    <span className="evalws-dispo-name">
                      {name}
                      {!isMarkup && r.item.principal ? (
                        <span className="evalws-dispo-star" title="Principal / main entry">★</span>
                      ) : null}
                      {isDoor && (
                        <span className="evalws-dispo-chip is-door">door · {r.item.padaOrZone}</span>
                      )}
                      {r.kind === 'auo' && (
                        <span className={`evalws-dispo-chip ${statusChipClass(r.item.status)}`}>{r.item.status ?? 'review'}</span>
                      )}
                      {isMarkup && (
                        <span className="evalws-dispo-chip is-info">markup</span>
                      )}
                    </span>
                    <span className="evalws-dispo-sub">
                      {isMarkup
                        ? (r.markup.note || 'annotation — no engine verdict')
                        : isDoor
                          ? `${r.item.typeLabel} · pada ${r.item.padaOrZone}`
                          : r.item.padaOrZone}
                    </span>
                  </span>
                  <span className="evalws-dispo-chev" aria-hidden="true"><IconChevron open={open} /></span>
                </button>
              </div>

              {/* expanded FEATURE CARD — same click-to-expand for all three types */}
              {open && r.kind === 'auo' && (() => {
                const mark = auoById.get(r.key);
                if (!mark) return null;
                return (
                  <div className="evalws-dispo-auocard">
                    {/* NO per-card engine trigger — resolution is driven by the ONE
                        list-level button (top). This card just DISPLAYS its result:
                        the byte-identical AuoCardBody renders the engine rulings once
                        they land. */}
                    <AuoCardBody
                      num={r.seqNo}
                      mark={mark}
                      selected={selected}
                      paletteItems={paletteItems}
                      isInPalette={(nm) => paletteLabelSet.has(nm.trim().toLowerCase())}
                      onSelect={onSelect}
                      onSetName={onSetName}
                      onSetType={onSetType}
                      onSetNameType={onSetNameType}
                      onSetZones={onSetZones}
                      onSetShape={onSetShape}
                      onRotate={onRotate}
                      onConfirm={onConfirm}
                      onReject={onReject}
                      onReopen={onReopen}
                      onRemove={onRemove}
                      isAdmin={isAdmin}
                      onCorrectDisposition={onCorrectDisposition}
                      onInlineAdd={onInlineAdd}
                    />
                  </div>
                );
              })()}

              {open && isDoor && (
                <DoorCard
                  it={r.item}
                  resolved={asked}
                  onSetDoorType={onSetDoorType}
                  onSetPrincipal={onSetPrincipal}
                  onRemoveDoor={onRemoveDoor}
                />
              )}

              {open && isMarkup && (
                <MarkupCard
                  m={r.markup}
                  onSetMarkupNote={onSetMarkupNote}
                  onSetMarkupLabel={onSetMarkupLabel}
                  onRemoveMarkup={onRemoveMarkup}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
